import { Box, Button, CircularProgress, FormControl, MenuItem, Select, SelectChangeEvent, Stack, Typography, useTheme, Chip } from "@mui/material";
import "./MultiSectionVisualization.css";
import * as d3 from "d3";
import { useEffect, useMemo, useState } from "react";
import { MultiSectionVisualization } from "./MultiSectionVisualization";
import { IPositionProps} from "../../../Models/IUnitFlowDefinition";
import { useMsal } from "@azure/msal-react";
import { ApiService } from "../../../Services/ApiService";
import logger from '../../../Services/LoggingService';
import { IUnitBaseConfiguration } from "../../../Models/IUnitConfiguration";
import { SxProps, Theme } from "@mui/material/styles";

interface SectionPositionProps {
    id?: string;
    positionProperties: IPositionProps[];
    propertyName: string;
    color?: string;
}

interface MultiSectionFlowchartProps {
    sectionCount: number;
    selectableProperties?: string[];
    unitInputConfiguration: IUnitBaseConfiguration;
    scaling?: number;
    sectionMargin?: number;
    isEdit?: boolean;
    isNewCreated?: boolean;
}

// Default positions for the interactive circles
const defaultPositions: IPositionProps[] = [
    { x: 10, y: 25, id: 0 },   // left top-outside-in
    { x: 40, y: -10, id: 1 },  // left top-in
    { x: 40, y: 25, id: 2 },   // left top
    { x: 10, y: 60, id: 3 },   // left outside-in
    { x: 40, y: 60, id: 4 },   // left bottom-outside-in
    { x: 40, y: 100, id: 5 },  // 
    { x: 190, y: -10, id: 6 },
    { x: 220, y: 25, id: 7 },
    { x: 190, y: 25, id: 8 },
    { x: 220, y: 60, id: 9 },
    { x: 190, y: 60, id: 10 },
    { x: 190, y: 100, id: 11 },
];

/**
 * MultiSectionFlowchart component
 * Displays heat exchanger sections with interactive flow line editing
 */
export const MultiSectionFlowchart: React.FC<MultiSectionFlowchartProps> = ({
    sectionCount,
    selectableProperties = [],
    unitInputConfiguration,
    scaling = 1,
    isEdit = false,
    isNewCreated = false
}) => {
    const ctx = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const theme = useTheme();

    const [isLoading, setIsLoading] = useState(true);
    const [sections, setSections] = useState<number[]>([]);
    const [selectedProperty, setSelectedProperty] = useState<string>('');
    const [secProps, setSecProps] = useState<Record<string, SectionPositionProps>>({});
    const [step, setStep] = useState<Record<string, number>>({});
    const [availableProperties, setAvailableProperties] = useState<string[]>(selectableProperties || []);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const activeSection = secProps[selectedProperty];

    // Calculate responsive sizes based on section count
    const sectionWidth = useMemo(() => {
        // Base width adjustment based on section count
        if (sectionCount === 1) return 180;
        if (sectionCount === 2) return 160;
        if (sectionCount === 3) return 150;
        if (sectionCount === 4) return 130;
        if (sectionCount >= 5) return 110;
        return 180; // Default
    }, [sectionCount]);

    // Styles
    const styles = {
        // Container styles
        container: {
            mb: 4,
            mt: 2,
            width: '100%',
            position: 'relative',
            px: 1,
            overflow: 'hidden',
            minWidth: sectionCount > 3 ? `${sectionCount * 140}px` : 'auto',
        },
        sectionContainer: {
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
        sectionRow: {
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexDirection: {
                xs: sectionCount > 3 ? 'row' : 'column', // Keep in row for many sections even on mobile
                sm: sectionCount > 3 ? 'row' : (sectionCount > 2 ? 'column' : 'row'),
                md: 'row', // Horizontal on medium screens and above
            },
            flexWrap: 'nowrap', // Prevent wrapping to keep all sections in one row
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            py: 2,
        },
        sectionItem: {
            width: sectionWidth,
            height: 100,
            position: 'relative',
            mx: 0.5,
            my: 0.5,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'visible',
        },
        heatExchangerWrapper: {
            position: 'relative',
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        
        // Interactive elements styles
        circlesContainer: {
            position: 'absolute',
            top: 5,
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${220 * scaling}px`,
            zIndex: 10,
        },
        
        // Controls and UI styles
        controlsContainer: {
            mb: 2.5,
            mt: 0,
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'flex-start',
            px: 2,
            position: 'relative',
            width: '100%',
            flexDirection: 'column',
        },
        controlsInner: {
            mt: 2.5,
            width: '100%',
            maxWidth: 900,
        },
        propertySelect: {
            minWidth: 200,
            mt: 1,
            mb: 2,
        },
        mappedProperties: {
            maxWidth: 900,
            flexWrap: 'wrap',
            gap: 1.25,
            mt: 1,
            mb: 2,
        },
        propertyChip: {
            p: 0.75,
            m: 0.5,
            cursor: 'pointer',
            fontSize: '0.8rem',
        },
        buttonContainer: {
            mt: 2.5,
            display: 'flex',
            gap: 1.25,
        },
        editButton: {
            textTransform: 'none',
            mb: 2,
        },
        saveButton: {
            textTransform: 'none',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
        interactiveCircle: (isSelected: boolean, isCurrentSelection: boolean, color?: string): SxProps<Theme> => ({
            color: isSelected ? 'white' : 'black',
            bgcolor: isSelected 
                ? isCurrentSelection 
                    ? 'orange' 
                    : color 
                : theme.palette.grey[300],
            boxSizing: 'border-box',
            position: 'absolute',
            width: '18px',
            height: '18px',
            textAlign: 'center',
            // paddingTop: '1px',
            fontSize: '0.65rem',
            lineHeight: '0.65rem', 
            justifyContent: 'center',
            borderRadius: '9px',
            zIndex: 1000,
            transform: 'translate(-9px, -9px)', // Center the dot on the coordinates (16px / 2 = 8px)
            border: `solid 1px ${theme.palette.secondary.main}`,
            ...(isCurrentSelection && {
                // Combine translate with scale animation to maintain position
                animation: 'pulse-translate 1s infinite',
                '@keyframes pulse-translate': {
                    '0%': {
                        transform: 'translate(-9px, -9px) scale(1)',
                    },
                    '50%': {
                        transform: 'translate(-9px, -9px) scale(1.2)',
                    },
                    '100%': {
                        transform: 'translate(-9px, -9px) scale(1)',
                    },
                },
            }),
            '&:hover': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
                cursor: 'pointer',
            },
        }),
    };

    /**
     * Load existing flowchart data from API
     */
    const loadFlowchartData = async () => {
        if (!unitInputConfiguration || !(isEdit || isNewCreated)) return;
        
        const configWithSectionCount = {
            ...unitInputConfiguration,
            sectionCount
        };
        
        try {
            setIsLoading(true);
            const response = await apiService.getAsync(
                'settings/unitFlowDefinitions', 
                configWithSectionCount
            );
            
            if (!response.ok) {
                const error = await response.text();
                logger.error('Failed to get flowchart from API', new Error(error), {
                    status: response.status,
                    statusText: response.statusText
                }, {
                    component: 'MultiSectionFlowchart',
                    method: 'loadFlowchartData'
                });
                return;
            }

            const result: SectionPositionProps[] = await response.json();
            if (!result || result.length === 0) return;
            
            // Process and organize the results
            const sectionPositions: Record<string, SectionPositionProps> = {};
            const propertyList: string[] = [...availableProperties];
            const stepCounts: Record<string, number> = {};
            
            for (const item of result) {
                if (!propertyList.includes(item.propertyName)) {
                    propertyList.push(item.propertyName);
                }
                stepCounts[item.propertyName] = item.positionProperties.filter(x => x.isSelected).length;
                sectionPositions[item.propertyName] = item;
            }

            setStep(stepCounts);
            setSecProps(sectionPositions);
            setAvailableProperties(propertyList);
            
            // Select the first property if none is selected
            if (!selectedProperty && propertyList.length > 0) {
                setSelectedProperty(propertyList[0]);
            }
        } catch (error) {
            logger.error("Failed to load flowchart", error as Error, null, {
                component: 'MultiSectionFlowchart',
                method: 'useEffect'
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Toggle selection state of a button/position
     */
    const togglePositionSelection = (index: number, property?: string) => {
        if (!property) return;
        
        const currentSelections = [...activeSection.positionProperties];
        let currentStep = step[property] || 0;
        const positionTarget = currentSelections.find(x => x.id === index);
        
        if (!positionTarget) return;
        
        if (positionTarget.isSelected) {
            positionTarget.isSelected = false;
            positionTarget.step = undefined;
            currentStep = currentStep - 1;
        } else {
            positionTarget.isSelected = true;
            positionTarget.step = currentStep;
            currentStep = currentStep + 1;
        }

        setStep({ ...step, [property]: currentStep });
        setSecProps({
            ...secProps,
            [property]: { 
                ...secProps[property], 
                positionProperties: currentSelections 
            }
        });
    };

    /**
     * Create default positions for all sections
     */
    const getDefaultPositions = (): IPositionProps[] => {
        const allSectionPositions: IPositionProps[] = [];  
        
        for (let i = 0; i < sectionCount; i++) {
            const startIndex = i === 0 ? i : 12 * i;
            for (let j = 0; j < 12; j++) {
                allSectionPositions.push({ 
                    ...defaultPositions[j], 
                    id: j + startIndex 
                });
            }
        }

        return allSectionPositions;
    };

    /**
     * Draw flow lines using D3.js
     */
    const drawFlowLines = () => {
        if (isLoading) return;
        
        // Clear all previous SVG lines
        d3.selectAll("svg.flow-svg line").remove();
        
        const sectionProperties = Object.values(secProps).filter(x => x !== undefined);
        
        for (const target of sectionProperties) {
            // First, process lines within each section
            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
                const startIndex = sectionIndex === 0 ? 0 : 12 * sectionIndex;
                const endIndex = startIndex + 12;
                
                const positions = target.positionProperties
                    .filter(x => x?.isSelected && x.id >= startIndex && x.id < endIndex)
                    .sort((a, b) => a.step! - b.step!);
                
                if (positions.length < 2) continue;
                
                const svg = d3.select(`#flow-svg-${sectionIndex}`);
                
                for (let j = 0; j < positions.length - 1; j++) {
                    const lineColor = target.propertyName.toLowerCase().includes('hot') 
                        ? '#d32f2f' 
                        : '#1976d2';
                    
                    svg.append("line")
                        .attr("x1", positions[j].x)
                        .attr("y1", positions[j].y)
                        .attr("x2", positions[j + 1].x)
                        .attr("y2", positions[j + 1].y)
                        .attr("stroke", lineColor)
                        .attr("stroke-width", 2)
                        .attr("stroke-dasharray", "4,2")
                        .attr("class", "flow-line");
                }
            }
            
            // Now draw connecting lines across sections
            // Get all selected positions across all sections for this property
            const allSelectedPositions = target.positionProperties
                .filter(x => x?.isSelected)
                .sort((a, b) => a.step! - b.step!);
                
            if (allSelectedPositions.length < 2) continue;
            
            // Draw continuous lines that connect across sections
            for (let i = 0; i < allSelectedPositions.length - 1; i++) {
                const pos1 = allSelectedPositions[i];
                const pos2 = allSelectedPositions[i + 1];
                
                // Calculate which section each position belongs to
                const section1 = Math.floor(pos1.id! / 12);
                const section2 = Math.floor(pos2.id! / 12);
                
                // If positions are in different sections, draw a connecting line   
                // TODO: IN FUTURE HERE WE CAN TRY TO INTRODUCE 3 SECTION PASTURIZER ADDITIONAL COLORS
                if (section1 !== section2) {
                    const lineColor = target.propertyName.toLowerCase().includes('hot') 
                        ? '#d32f2f' 
                        : '#1976d2';
                    
                    // For positions on the sides of the heat exchanger
                    const isRightSide1 = pos1.x >= 150;
                    const isLeftSide2 = pos2.x <= 50;
                    
                    // Draw connecting line in the first section SVG
                    const svg1 = d3.select(`#flow-svg-${section1}`);
                    svg1.append("line")
                        .attr("x1", pos1.x)
                        .attr("y1", pos1.y)
                        .attr("x2", isRightSide1 ? 200 : pos1.x) // Extend to right edge if on right side
                        .attr("y2", pos1.y)
                        .attr("stroke", lineColor)
                        .attr("stroke-width", 2)
                        .attr("stroke-dasharray", "4,2")
                        .attr("class", "flow-line connector-line");
                    
                    // Draw connecting line in the second section SVG
                    const svg2 = d3.select(`#flow-svg-${section2}`);
                    svg2.append("line")
                        .attr("x1", isLeftSide2 ? 0 : pos2.x) // Start from left edge if on left side
                        .attr("y1", pos2.y)
                        .attr("x2", pos2.x)
                        .attr("y2", pos2.y)
                        .attr("stroke", lineColor)
                        .attr("stroke-width", 2)
                        .attr("stroke-dasharray", "4,2")
                        .attr("class", "flow-line connector-line");
                    
                    // If there are sections in between, draw lines across those too
                    if (section2 - section1 > 1) {
                        for (let midSection = section1 + 1; midSection < section2; midSection++) {
                            const svgMid = d3.select(`#flow-svg-${midSection}`);
                            svgMid.append("line")
                                .attr("x1", 0) // From left edge
                                .attr("y1", pos1.y)
                                .attr("x2", 200) // To right edge
                                .attr("y2", pos1.y)
                                .attr("stroke", lineColor)
                                .attr("stroke-width", 2)
                                .attr("stroke-dasharray", "4,2")
                                .attr("class", "flow-line connector-line");
                        }
                    }
                }
            }
        }
    };

    /**
     * Set the selected property and initialize if needed
     */
    const changeSelectedProperty = (property: string) => {
        setSelectedProperty(property);
        
        let activeSec = secProps[property];
        if (!activeSec) {
            activeSec = {
                propertyName: property,
                color: property.toLowerCase().includes('hot') 
                    ? theme.palette.error.main 
                    : theme.palette.primary.main,
                positionProperties: getDefaultPositions()
            };
            
            setSecProps(prev => ({ 
                ...prev, 
                [property]: activeSec 
            }));
        }
        
        const currentStep = step[property] || 0;
        setStep(prev => ({ ...prev, [property]: currentStep }));
    };

    /**
     * Save flow definitions to API
     */
    const saveFlowDefinitions = async () => {
        setIsSaving(true);
        try {
            for (const key of Object.keys(secProps)) {
                const payload = {
                    ...unitInputConfiguration,
                    propertyName: key,
                    color: secProps[key].color!,
                    positionProperties: secProps[key].positionProperties,
                    id: secProps[key].id
                };

                let response;
                if (payload.id) {
                    response = await apiService.putAsync(
                        payload, 
                        `settings/unitFlowDefinitions/${payload.id}`
                    );
                } else {
                    response = await apiService.postAsync(
                        payload, 
                        'settings/unitFlowDefinitions', 
                        unitInputConfiguration
                    );
                }
                
                if (!response.ok) {
                    const error = await response.text();
                    throw new Error('Could not save flowchart - ' + error);
                }
            }
            
            await loadFlowchartData();
        } catch (error) {
            logger.error("Failed to save flowchart", error as Error, null, {
                component: 'MultiSectionFlowchart',
                method: 'saveFlowchart'
            });
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Toggle editing mode
     */
    const toggleEditingMode = () => {
        const shouldBeEditing = !isEditing;
        
        if (shouldBeEditing) {
            if (Object.keys(secProps).length > 0) {
                // Select first property if none selected
                const firstKey = Object.keys(secProps)[0];
                setSelectedProperty(firstKey);
            } else if (availableProperties.length > 0) {
                setSelectedProperty(availableProperties[0]);
            }
        }

        setIsEditing(shouldBeEditing);
    };

    /**
     * Render a single interactive circle
     */
    const renderInteractiveCircle = (position: IPositionProps, isCurrentSelection: boolean) => (
        <Box
            key={`section-button-${position.id}`}
            id={`s-button-${position.id}`}
            onClick={() => togglePositionSelection(position.id, selectedProperty)}
            sx={{
                left: `${position.x * scaling}px`,
                top: `${position.y}px`,
                ...styles.interactiveCircle(
                    !!position.isSelected, 
                    isCurrentSelection, 
                    activeSection?.color
                )
            }}
            className="clickable-flow-button"
        >
            {position.isSelected ? position.step : ''}
        </Box>
    );

    // Initialize component
    useEffect(() => {
        loadFlowchartData();
    }, []);

    // Update sections when section count changes
    useEffect(() => {
        setSections(Array.from({ length: sectionCount }, (_, i) => i));
        setIsLoading(false);
    }, [sectionCount]);

    // Draw flow lines when data changes
    useEffect(() => {
        drawFlowLines();
    }, [secProps, isLoading, sections]);

    return (
        <Stack sx={{ width: '100%' }}>
            <Box sx={{
                ...styles.container,
                width: '100%',
                maxWidth: '100%',
                paddingBottom: '10px'
            }}>
                {isLoading ? (
                    <Box display="flex" alignItems="center">
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        <Typography>Loading...</Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        ...styles.sectionContainer,
                        width: '100%',
                    }}>
                        <Box sx={{
                            ...styles.sectionRow,
                            width: '100%',
                            justifyContent: sectionCount > 3 ? 'space-between' : 'center',
                        }}>
                            {sections.map((section, index) => {
                                const startIndex = index === 0 ? index : 12 * index;
                                const isFirst = index === 0;
                                const isLast = index === sections.length - 1;

                                return (
                                    <Box key={`section-${index}`} sx={styles.sectionItem}>
                                        <Box sx={styles.heatExchangerWrapper}>
                                            {/* Interactive circles */}
                                            {isEditing && activeSection && (
                                                <Box sx={styles.circlesContainer}>
                                                    {activeSection?.positionProperties
                                                        .slice(startIndex, startIndex + 12)
                                                        .map((position) => {
                                                            const isCurrentSelection = 
                                                                position.step === step[selectedProperty] - 1;
                                                                
                                                            return renderInteractiveCircle(
                                                                position, 
                                                                isCurrentSelection
                                                            );
                                                        })}
                                                </Box>
                                            )}
                                            
                                            {/* SVG for flow lines - positioned before the visualization so it's behind */}
                                            <Box
                                                component="svg"
                                                className="flow-svg heat-exchanger-svg" 
                                                id={`flow-svg-${index}`}
                                                sx={{ 
                                                    position: 'absolute', 
                                                    top: 5, 
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: `${220 * scaling}px`,
                                                    // height: '100px',
                                                    zIndex: 5,
                                                    pointerEvents: 'none'
                                                }}
                                                viewBox="0 0 220 100"
                                                preserveAspectRatio="none"
                                            />
                                            
                                            {/* Heat exchanger visualization */}
                                            <MultiSectionVisualization
                                                scaling={scaling}
                                                leftEnding={isFirst}
                                                rightEnding={isLast}
                                                label={`${sections.length - index}`}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
            
            {scaling === 1 && (
                <Box sx={styles.controlsContainer}>
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={toggleEditingMode}
                        startIcon={isEditing ? <span>&#x1F441;&#xFE0F;</span> : <span>&#x270F;&#xFE0F;</span>}
                        sx={styles.editButton}
                    >
                        {isEditing ? "Hide editing nodes" : "Edit flowchart"}
                    </Button>

                    {availableProperties.length > 0 && isEditing && (
                        <Box sx={styles.controlsInner}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Input or output to map
                            </Typography>
                            
                            <FormControl sx={styles.propertySelect}>
                                <Select
                                    value={selectedProperty}
                                    onChange={(e: SelectChangeEvent) => changeSelectedProperty(e.target.value)}
                                    size="small"
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        <em>Select a property</em>
                                    </MenuItem>
                                    {availableProperties.map((prop) => (
                                        <MenuItem key={prop} value={prop}>
                                            {prop}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <Typography variant="subtitle1" fontWeight="medium">
                                Mapped properties:
                            </Typography>
                            
                            <Stack direction="row" sx={styles.mappedProperties}>
                                {Object.keys(secProps).map((key) => {
                                    const section = secProps[key];
                                    if (!section || 
                                        section.positionProperties.filter(x => x.isSelected).length === 0) {
                                        return null;
                                    }
                                    
                                    const isHot = key.toLowerCase().includes('hot');
                                    
                                    return (
                                        <Chip
                                            key={key}
                                            color={isHot ? "error" : "primary"}
                                            variant={selectedProperty === key ? "filled" : "outlined"}
                                            sx={styles.propertyChip}
                                            onClick={() => changeSelectedProperty(key)}
                                            label={key}
                                            size="small"
                                        />
                                    );
                                })}
                            </Stack>
                            
                            <Box sx={styles.buttonContainer}>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    disabled={isSaving}
                                    onClick={saveFlowDefinitions}
                                    startIcon={<span>&#x1F4BE;</span>}
                                    sx={styles.saveButton}
                                >
                                    Save flowchart
                                </Button>
                                
                                {isSaving && (
                                    <Box display="flex" alignItems="center">
                                        <CircularProgress size={24} sx={{ mr: 1 }} />
                                        <Typography>Saving...</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    );
}; 